import footerLogo from "@/assets/logow.webp";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Send,
  Twitter,
  Youtube,
} from "lucide-react";

const SITE_URL = "https://squaretrigger.com";
const LOGO_URL = footerLogo;
const ORGANIZATION_LOGO_URL = `${SITE_URL}${LOGO_URL}`;

const serviceLinks = [
  {
    name: "Website Development",
    path: "/services/website-development",
  },
  {
    name: "SEO Services",
    path: "/services/search-engine-optimization-company",
  },
  {
    name: "Social Media Marketing",
    path: "/services/social-media-marketing-agency",
  },
  {
    name: "Custom Tool Creation",
    path: "/services/custom-tool-creation",
  },
  {
    name: "Maintenance & Support",
    path: "/services/website-maintenance-support",
  },
];

const companyLinks = [
  {
    name: "About Us",
    path: "/about",
  },
  {
    name: "Contact",
    path: "/contact",
  },
  {
    name: "Our Work",
    path: "/work",
  },
  {
    name: "Get Started",
    path: "/get-started",
  },
];

const resourceLinks = [
  {
    name: "Blog",
    path: "/blog",
  },
  {
    name: "FAQs",
    path: "/resources-faqs",
  },
];

const legalLinks = [
  {
    name: "Privacy Policy",
    path: "/privacy-policy",
  },
  {
    name: "Terms of Service",
    path: "/terms-of-service",
  },
];

const serviceAreaLinks = [
  {
    name: "Surrey",
    path: "/digital-marketing-agency-surrey",
  },
  {
    name: "Burnaby",
    path: "/digital-marketing-agency-burnaby",
  },
  {
    name: "Richmond",
    path: "/digital-marketing-agency-richmond",
  },
  {
    name: "North Vancouver",
    path: "/top-digital-agency-in-north-vancouver",
  },
  {
    name: "Coquitlam",
    path: "/digital-marketing-agency-coquitlam",
  },
  {
    name: "Langley",
    path: "/digital-marketing-agency-langley",
  },
];

const socialLinks = [
  {
    name: "Facebook",
    icon: Facebook,
    url: "https://www.facebook.com/profile.php?id=61585572042270",
    ariaLabel: "Square Trigger Facebook link",
  },
  {
    name: "Twitter",
    icon: Twitter,
    url: "",
    ariaLabel: "Square Trigger Twitter link",
  },
  {
    name: "Instagram",
    icon: Instagram,
    url: "https://www.instagram.com/square_trigger.official/",
    ariaLabel: "Square Trigger Instagram link",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    url: "https://www.linkedin.com/company/square-trigger/",
    ariaLabel: "Square Trigger LinkedIn link",
  },
  {
    name: "YouTube",
    icon: Youtube,
    url: "#",
    ariaLabel: "Square Trigger YouTube link",
  },
];

export const Footer = () => {
  const [email, setEmail] = React.useState("");
  const navigate = useNavigate();

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();

    alert("Thank you for subscribing!");
    setEmail("");
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleInternalNavigation = (event, path) => {
    event.preventDefault();
    navigate(path);
    scrollToTop();
  };

  return (
    <footer className="relative overflow-hidden bg-foreground text-background">
      <div className="container-custom pointer-events-auto relative z-20">
        {/* Newsletter Section */}
        <div className="border-b border-background/10 py-12 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <motion.h3
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              className="mb-4 font-display text-2xl font-bold md:text-3xl"
            >
              Stay Ahead With Digital Insights
            </motion.h3>

            <motion.p
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                delay: 0.1,
              }}
              className="mb-8 px-2 text-background/70"
            >
              Get useful updates on website development, SEO, digital strategy,
              and business growth.
            </motion.p>

            <motion.form
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                delay: 0.2,
              }}
              onSubmit={handleNewsletterSubmit}
              className="mx-auto flex max-w-md flex-col gap-3 sm:flex-row"
            >
              <Input
                type="email"
                aria-label="Email address for newsletter"
                placeholder="Enter your email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                className="h-11 border-background/20 bg-background/10 text-background placeholder:text-background/50 focus:border-primary"
                required
              />

              <Button
                variant="gradient"
                type="submit"
                className="h-11 shrink-0"
              >
                Subscribe

                <Send className="ml-2 h-4 w-4" />
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Main Footer */}
        <div className="grid grid-cols-1 items-start gap-12 py-12 md:grid-cols-2 md:gap-10 md:py-16 lg:grid-cols-5 lg:gap-12">
          {/* Brand */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left lg:col-span-2">
            <Link
              to="/"
              onClick={(event) => {
                handleInternalNavigation(event, "/");
              }}
              className="relative z-20 mb-6 inline-block"
            >
              <img
                src={LOGO_URL}
                alt="Square Trigger"
                width="1080"
                height="540"
                loading="lazy"
                decoding="async"
                className="mx-auto h-12 w-auto object-contain md:mx-0 md:h-14"
              />
            </Link>

            <div className="mb-3">
              <h4 className="font-display text-xl font-semibold text-background">
                Square Trigger
              </h4>

              <p className="mt-1 text-sm text-background/70">
                Digital Agency in Vancouver, BC
              </p>
            </div>

            <p className="mb-6 max-w-sm leading-relaxed text-background/70">
              Website development, SEO services, social media marketing, custom
              tool creation, and ongoing support for businesses that want a
              stronger online presence.
            </p>

            {/* Service Areas */}
            <div className="mt-6">
              <h4 className="mb-6 font-display text-xl font-semibold text-background">
  Service Areas
</h4>

              <div className="grid max-w-[520px] grid-cols-1 gap-2 sm:grid-cols-3">
                {serviceAreaLinks.map((serviceArea) => (
                  <Link
                    key={serviceArea.name}
                    to={serviceArea.path}
                    onClick={(event) => {
                      handleInternalNavigation(
                        event,
                        serviceArea.path
                      );
                    }}
                    className="inline-flex items-center justify-center rounded-full border border-white/10 px-3 py-1.5 text-sm text-white/70 transition-all hover:border-primary hover:bg-white/5 hover:text-primary"
                  >
                    {serviceArea.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <h4 className="mb-6 font-display text-xl font-semibold text-background">
              Services
            </h4>

            <ul className="space-y-4">
              {serviceLinks.map((serviceLink) => (
                <li key={serviceLink.name}>
                  <Link
                    to={serviceLink.path}
                    onClick={(event) => {
                      handleInternalNavigation(
                        event,
                        serviceLink.path
                      );
                    }}
                    className="relative z-20 inline-block cursor-pointer px-2 py-1 text-background/70 transition-colors hover:text-background lg:px-0"
                  >
                    {serviceLink.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <h4 className="mb-6 font-display text-xl font-semibold text-background">
              Company
            </h4>

            <ul className="flex w-full flex-col items-center space-y-4 lg:items-start">
              {companyLinks.map((companyLink) => (
                <li key={companyLink.name}>
                  <Link
                    to={companyLink.path}
                    onClick={(event) => {
                      handleInternalNavigation(
                        event,
                        companyLink.path
                      );
                    }}
                    className="relative z-20 inline-block cursor-pointer py-1 text-background/70 transition-colors hover:text-background"
                  >
                    {companyLink.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex w-full max-w-[240px] flex-col items-center space-y-4 text-center lg:items-start lg:text-left">
              <a
                href="mailto:info@squaretrigger.com"
                className="relative z-20 flex w-full items-start justify-center gap-3 text-background/70 transition-colors hover:text-background lg:justify-start"
              >
                <Mail className="mt-1 h-4 w-4 shrink-0" />

                <span className="break-words leading-relaxed">
                  info@squaretrigger.com
                </span>
              </a>

              <a
                href="tel:236869335"
                className="relative z-20 flex w-full items-center justify-center gap-3 text-background/70 transition-colors hover:text-background lg:justify-start"
              >
                <Phone className="h-4 w-4 shrink-0" />

                <span>236-869-335</span>
              </a>

              <div className="flex w-full items-center justify-center gap-3 text-background/70 lg:justify-start">
                <MapPin className="h-4 w-4 shrink-0" />

                <span>Vancouver, BC</span>
              </div>
            </div>
          </div>

          {/* Resources and Legal */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <h4 className="mb-6 font-display text-xl font-semibold text-background">
              Resources
            </h4>

            <ul className="mb-10 space-y-4">
              {resourceLinks.map((resourceLink) => (
                <li key={resourceLink.name}>
                  <Link
                    to={resourceLink.path}
                    onClick={(event) => {
                      handleInternalNavigation(
                        event,
                        resourceLink.path
                      );
                    }}
                    className="relative z-20 inline-block cursor-pointer px-2 py-1 text-background/70 transition-colors hover:text-background lg:px-0"
                  >
                    {resourceLink.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="mb-6 font-display text-xl font-semibold text-background">
              Legal
            </h4>

            <ul className="space-y-4">
              {legalLinks.map((legalLink) => (
                <li key={legalLink.name}>
                  <Link
                    to={legalLink.path}
                    onClick={(event) => {
                      handleInternalNavigation(
                        event,
                        legalLink.path
                      );
                    }}
                    className="relative z-20 inline-block cursor-pointer px-2 py-1 text-background/70 transition-colors hover:text-background lg:px-0"
                  >
                    {legalLink.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 py-8">
          <div className="flex flex-col items-center justify-center gap-6 text-center md:flex-row md:justify-between">
            <div className="text-sm text-background/80">
              © {new Date().getFullYear()} Square Trigger. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {socialLinks.map((socialLink) => {
                const Icon = socialLink.icon;

                if (!socialLink.url) {
                  return null;
                }

                return (
                  <motion.a
                    key={socialLink.name}
                    href={socialLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-20 rounded-lg bg-background/10 p-3 text-background/70 transition-all duration-300 hover:bg-primary hover:text-primary-foreground"
                    whileHover={{
                      scale: 1.08,
                    }}
                    whileTap={{
                      scale: 0.95,
                    }}
                    aria-label={socialLink.ariaLabel}
                    title={socialLink.ariaLabel}
                  >
                    <Icon
                      className="h-4 w-4"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </motion.a>
                );
              })}
            </div>

            <div className="text-sm text-background/80">
              Built for businesses that want a stronger online presence.
            </div>
          </div>
        </div>
      </div>

      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Square Trigger",
          description:
            "Website development, SEO services, social media marketing, custom tool creation, and ongoing support for businesses that want a stronger online presence.",
          url: SITE_URL,
          logo: ORGANIZATION_LOGO_URL,
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "236-869-335",
            contactType: "customer service",
            email: "info@squaretrigger.com",
          },
          sameAs: socialLinks
            .filter(
              (socialLink) =>
                socialLink.url &&
                socialLink.url !== "#"
            )
            .map((socialLink) => socialLink.url),
        })}
      </script>
    </footer>
  );
};
